import React, { useState, useRef, useEffect } from "react";
import style from "./CommonTooltip.module.css";

interface CommonTooltipProps {
  content: string | React.ReactNode;
  children: React.ReactNode;
  className?: string;
  maxWidth?: number;
  delay?: number;
}

const CommonTooltip: React.FC<CommonTooltipProps> = ({
  content,
  children,
  className = "",
  maxWidth = 250,
  delay = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const updateTooltipPosition = (e: MouseEvent) => {
    if (tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const leftOffset = 15;
      const topOffset = 20;

      // Calcular posición inicial
      let left = e.clientX + leftOffset; // Aumentamos el offset horizontal
      let top = e.clientY + topOffset; // Aumentamos el offset vertical

      // Ajustes para mantener el tooltip dentro de la ventana
      if (left + tooltipRect.width > window.innerWidth) {
        left = e.clientX - tooltipRect.width - leftOffset;
      }

      if (top + tooltipRect.height > window.innerHeight) {
        top = e.clientY - tooltipRect.height - topOffset;
      }

      // Asegurar que no se salga por la izquierda o arriba
      left = Math.max(leftOffset, left);
      top = Math.max(topOffset, top);

      setPosition({ top, left });
    }
  };

  const handleMouseEnter = () => {
    if (delay) {
      timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
    } else {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    const containerElement = containerRef.current;

    if (containerElement && isVisible) {
      containerElement.addEventListener("mousemove", updateTooltipPosition);
      return () => {
        containerElement.removeEventListener(
          "mousemove",
          updateTooltipPosition
        );
      };
    }
  }, [isVisible]);

  return (
    <div
      ref={containerRef}
      className={`${style.tooltipContainer} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`${style.tooltip} ${isVisible ? style.visible : ""}`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            maxWidth: `${maxWidth}px`,
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default CommonTooltip;
